<template>
  <div>
    <Modal
      :active="active"
      @close="closePopup"
      class="large without_footer"
      data-test="upload-modal"
    >
      <template v-slot:header>
        {{ $t("upload.header") }}
      </template>
      <template v-slot:body>
        <div class="inner">
          <div class="uploader">
            <FileInput
              v-if="files.length === 0"
              @submit="setFiles"
              :singleFile="singleFile"
              :mimeTypes="replacementTypes"
            />

            <SelectedFiles
              v-else
              v-model="files"
              @removeFile="removeFile"
              @addFiles="addFiles"
              @startUpload="startUpload"
              :fileStates="fileStates"
              :singleFile="singleFile"
              :mimeType="mimeType"
              :isRunning="isRunning"
            />
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { Modal } from "@/components";
import { ref, PropType } from "vue";
import {
  UploadService,
  AnalyticsService,
  EventType,
  UploadResponseData,
  MimeType,
} from "@/util";
import { auth } from "@/fire";
import { WorkspaceDocumentFragment } from "@/graphql/operations";
import { useStore } from "vuex";
import FileInput from "./FileInput.vue";
import SelectedFiles from "./SelectedFiles.vue";
import { ErrorType, FileState, UploadState } from "./state";
import { captureException } from "@sentry/vue";

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: false,
  },
  singleFile: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["close", "success"]);
const closePopup = () => {
  emit("close");
};

const handleSuccess = () => {
  emit("success");
  closePopup();
};

const store = useStore();

const mimeType = props.doc
  ? MimeType.getMimeType(
      props.doc.originalMimeType
        ? props.doc.originalMimeType
        : props.doc.mimeType,
    )
  : undefined;

const replacementTypes = props.doc
  ? MimeType.allowedReplacementTypes(
      props.doc.originalMimeType
        ? props.doc.originalMimeType
        : props.doc.mimeType,
    )
  : undefined;

const files = ref<Array<File>>([]);
const fileStates = ref<Array<FileState>>([]);
const setFiles = (fileList: File[]) => {
  files.value = Array.from(fileList);
  for (let i = 0; i < files.value.length; i++) {
    fileStates.value[i] = {
      progress: 0,
      uploadState: UploadState.Idle,
    };
  }
};

const addFiles = (fileList: FileList) => {
  const newFiles = Array.from(fileList);
  const newIdx = fileStates.value.length;

  files.value = [...files.value, ...newFiles];
  for (let i = newIdx; i < files.value.length; i++) {
    fileStates.value[i] = {
      progress: 0,
      uploadState: UploadState.Idle,
    };
  }
};

const removeFile = (idx: number) => {
  files.value!.splice(idx, 1);
  fileStates.value!.splice(idx, 1);
};

const isRunning = ref<boolean>(false);
const errorOccurred = ref<boolean>(false);
const startUpload = async (notify: boolean) => {
  const token = await auth.currentUser!.getIdToken();
  isRunning.value = true;

  if (!files.value) {
    throw ErrorType.NoFileSelected;
  }

  if (files.value.length === 0) {
    throw ErrorType.NoFileSelected;
  }

  for (let i = 0; i < files.value.length; i++) {
    try {
      const postgresId = await upload(token, files.value[i], i, notify);
    } catch (e) {
      fileStates.value[i].uploadState = UploadState.Error;
      fileStates.value[i].error = e as ErrorType;
      errorOccurred.value = true;
      captureException(e);
    }
  }

  if (!errorOccurred.value) {
    handleSuccess();
  }
};

async function upload(
  token: string,
  file: File,
  i: number,
  notify: Boolean,
): Promise<string> {
  if (replacementTypes && !MimeType.contains(replacementTypes, file.type)) {
    throw ErrorType.WrongMimeType;
  }

  fileStates.value[i].progress = 0;
  fileStates.value[i].uploadState = UploadState.Running;

  let intervalId: number | undefined = undefined;

  const onProgress = (progress: number) => {
    fileStates.value[i].progress = Math.round(progress * 0.8);

    if (progress === 100 && intervalId === undefined) {
      /* start synthetic progress */
      intervalId = window.setInterval(() => {
        if (fileStates.value[i].progress < 99) {
          fileStates.value[i].progress++;
        }
      }, 800);
    }
  };

  const formData = new FormData();
  formData.append("file", file);
  formData.append("sendNotification", notify.toString());
  formData.append("uploadSource", "local");

  let fileData: UploadResponseData;
  try {
    fileData = await UploadService.upload(
      formData,
      token,
      store.getters.currentWorkspace.id,
      props.doc?.id,
      onProgress,
    );
  } catch (e) {
    console.error(e);
    switch ((e as { status: number; message: string }).status) {
      case 400:
        throw ErrorType.MissingFile;
      case 401:
        throw ErrorType.Unauthorized;
      case 415:
        throw ErrorType.UnsupportedMimeType;
      case 413:
        throw ErrorType.MaxSizeExceeded;
      default:
        throw ErrorType.InternalServerError;
    }
  }

  const postgresId = fileData.id.toString();
  const firestoreId = fileData.firestore_id;

  clearInterval(intervalId);
  fileStates.value[i].progress = 100;
  fileStates.value[i].uploadState = UploadState.Success;

  AnalyticsService.logEvent(
    EventType.DocumentUploaded,
    `${file.name} (${file.name})`,
    JSON.stringify({
      id: postgresId,
      firestoreId: firestoreId,
    }),
  );

  return postgresId;
}
</script>

<style scoped>
.inner {
  height: 100%;
}

.uploader {
  text-align: center;
  line-height: 1.1em;
  height: 100%;
  position: relative;
}

h3 {
  font-size: 1em;
}

.drop_icon span {
  font-size: 3.3em;
  padding: 15px 0;
  color: var(--classic-text-secondary, rgba(25, 23, 17, 0.6));
}

.button {
  font-size: 1em;
  margin: 25px 0 5px 0;
}

.hidden {
  visibility: hidden;
  height: 0;
  width: 0;
}

.info {
  display: block;
  font-size: 0.8em;
}
</style>
